import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient, QueryClient } from '@tanstack/react-query';
import { adminApi, authApi } from '../services/api';
import useAuthStore from '../store/authStore';
import { formatBSDate } from '../utils/bs';
import toast from 'react-hot-toast';
import type {
  Plant,
  ScadaSettings,
  ScadaSettingsForm,
  ScadaTag,
  User,
  NotificationSetting,
  CreateUserForm,
} from '../types';

interface WithQC { qc: QueryClient }

export default function Admin() {
  const { user } = useAuthStore();
  const qc = useQueryClient();

  const { data: plantData } = useQuery<{ plant: Plant | null }>({
    queryKey: ['my-plant', user?.id],
    queryFn: () => adminApi.plant().then(r => r.data),
    staleTime: 10 * 60_000,
    enabled: !!user && user.role !== 'super_admin',
  });

  const plant   = plantData?.plant;
  const isScada = plant?.plant_type === 'scada';

  const TABS = isScada
    ? (['SCADA', 'Tags', 'Users', 'Notifications'] as const)
    : (['Users', 'Notifications'] as const);

  type TabName = typeof TABS[number];
  const [tab, setTab] = useState<TabName>(isScada ? 'SCADA' : 'Users');

  // Keep tab valid if plant_type changes while page is open
  const activeTab = (TABS as readonly string[]).includes(tab) ? tab : TABS[0];

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-bold text-slate-900">Admin Settings</h1>
        {plant && (
          <span className={`text-xs px-2.5 py-1 rounded-full font-medium ${
            isScada ? 'bg-brand-100 text-brand-700' : 'bg-slate-100 text-slate-600'
          }`}>
            {isScada ? '⚡ SCADA Plant' : '📝 Manual Plant'}
          </span>
        )}
      </div>

      <div className="flex gap-2 border-b border-hydro-border pb-3">
        {TABS.map((t) => (
          <button
            key={t}
            onClick={() => setTab(t as TabName)}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              activeTab === t
                ? 'bg-brand-600 text-white'
                : 'text-slate-600 hover:text-slate-900 hover:bg-slate-100'
            }`}
          >
            {t}
          </button>
        ))}
      </div>

      {activeTab === 'SCADA'         && <ScadaSettingsPanel qc={qc} />}
      {activeTab === 'Tags'          && <TagSettings qc={qc} />}
      {activeTab === 'Users'         && <UserSettings qc={qc} />}
      {activeTab === 'Notifications' && <NotificationSettings qc={qc} />}
    </div>
  );
}

// ─── SCADA Settings ───────────────────────────────────────────────────────────
function ScadaSettingsPanel({ qc }: WithQC) {
  const { data } = useQuery<ScadaSettings>({
    queryKey: ['scada-settings'],
    queryFn: () => adminApi.scadaSettings().then((r) => r.data.settings),
  });
  const [form, setForm] = useState<ScadaSettingsForm>({});

  const mut = useMutation({
    mutationFn: (d: ScadaSettingsForm) => adminApi.updateScada(d),
    onSuccess: () => {
      toast.success('Settings saved');
      qc.invalidateQueries({ queryKey: ['scada-settings'] });
    },
    onError: () => toast.error('Failed to save'),
  });

  const cfg: Partial<ScadaSettings> = { ...data, ...form };

  type ScadaTextField = 'connection_type' | 'endpoint_url' | 'poll_interval_ms' | 'reconnect_delay_ms';
  const fields: { label: string; key: ScadaTextField; type: 'text' | 'number' }[] = [
    { label: 'Connection Type',      key: 'connection_type',    type: 'text' },
    { label: 'Endpoint URL',         key: 'endpoint_url',       type: 'text' },
    { label: 'Poll Interval (ms)',   key: 'poll_interval_ms',   type: 'number' },
    { label: 'Reconnect Delay (ms)', key: 'reconnect_delay_ms', type: 'number' },
  ];

  return (
    <div className="card max-w-lg space-y-4">
      <h2 className="text-sm font-semibold text-slate-900">OPC UA / SCADA Connection</h2>
      {fields.map(({ label, key, type }) => (
        <div key={key}>
          <label className="block text-xs text-slate-500 mb-1.5">{label}</label>
          <input
            type={type}
            value={cfg[key] ?? ''}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              setForm((f) => ({ ...f, [key]: e.target.value }))
            }
            className="input"
          />
        </div>
      ))}
      <div className={`rounded-lg border p-3 ${cfg.auto_fetch ? 'border-brand-400 bg-brand-50/40' : 'border-hydro-border'}`}>
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm font-semibold text-slate-900">Auto-fetch from SCADA</p>
            <p className="text-xs text-slate-500">When enabled, data is pulled automatically on schedule</p>
          </div>
          <div
            onClick={() => setForm(f => ({ ...f, auto_fetch: !(cfg.auto_fetch ?? true) }))}
            className={`w-10 h-6 rounded-full transition-colors cursor-pointer relative flex-shrink-0 ${cfg.auto_fetch ? 'bg-brand-600' : 'bg-slate-300'}`}
          >
            <div className={`absolute top-1 w-4 h-4 bg-white rounded-full shadow transition-all ${cfg.auto_fetch ? 'left-5' : 'left-1'}`} />
          </div>
        </div>
      </div>
      <button onClick={() => mut.mutate(form)} disabled={mut.isPending} className="btn-primary">
        {mut.isPending ? 'Saving...' : 'Save Settings'}
      </button>
    </div>
  );
}

// ─── Tag Settings ─────────────────────────────────────────────────────────────
function TagSettings({ qc }: WithQC) {
  const { data, isLoading } = useQuery<ScadaTag[]>({
    queryKey: ['tags'],
    queryFn: () => adminApi.tags().then((r) => r.data.tags),
  });
  const [editing, setEditing] = useState<ScadaTag | null>(null);

  const mut = useMutation({
    mutationFn: ({ id, ...d }: Partial<ScadaTag> & { id: number }) => adminApi.updateTag(id, d),
    onSuccess: () => {
      toast.success('Tag updated');
      qc.invalidateQueries({ queryKey: ['tags'] });
      setEditing(null);
    },
    onError: () => toast.error('Failed'),
  });

  const tags = data ?? [];
  type EditableKey = 'display_name' | 'unit' | 'alarm_low' | 'alarm_high' | 'warn_low' | 'warn_high';
  const numericKeys: EditableKey[] = ['alarm_low', 'alarm_high', 'warn_low', 'warn_high'];
  const editableKeys: EditableKey[] = ['display_name', 'unit', 'alarm_low', 'alarm_high', 'warn_low', 'warn_high'];

  return (
    <div className="space-y-3">
      <div className="card p-0 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr className="border-b border-hydro-border text-slate-500 uppercase tracking-wider">
                {['Tag Name','Display','Unit','Alarm Low','Alarm High','Warn Low','Warn High','Active','Action'].map((h) => (
                  <th key={h} className="px-3 py-3 text-left">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                <tr><td colSpan={9} className="px-4 py-8 text-center text-slate-400">Loading...</td></tr>
              ) : tags.map((tag) =>
                editing?.id === tag.id ? (
                  <tr key={tag.id} className="border-b border-hydro-border bg-brand-600/5">
                    <td className="px-3 py-2 font-mono text-slate-600">{tag.tag_name}</td>
                    {editableKeys.map((k) => (
                      <td key={k} className="px-2 py-1">
                        <input
                          type={numericKeys.includes(k) ? 'number' : 'text'}
                          value={editing[k] ?? ''}
                          onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                            setEditing((s) => s ? { ...s, [k]: e.target.value } : s)
                          }
                          className="input py-1 text-xs w-20"
                        />
                      </td>
                    ))}
                    <td className="px-3 py-2">
                      <input
                        type="checkbox"
                        checked={!!editing.is_active}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                          setEditing((s) => s ? { ...s, is_active: e.target.checked } : s)
                        }
                      />
                    </td>
                    <td className="px-3 py-2 flex gap-1">
                      <button onClick={() => mut.mutate(editing)} className="text-xs px-2 py-1 rounded bg-emerald-600 text-white">Save</button>
                      <button onClick={() => setEditing(null)} className="text-xs px-2 py-1 rounded bg-slate-200 text-slate-700">Cancel</button>
                    </td>
                  </tr>
                ) : (
                  <tr key={tag.id} className="table-row">
                    <td className="px-3 py-2 font-mono text-slate-600 whitespace-nowrap">{tag.tag_name}</td>
                    <td className="px-3 py-2">{tag.display_name}</td>
                    <td className="px-3 py-2">{tag.unit}</td>
                    <td className="px-3 py-2 font-mono">{tag.alarm_low ?? '—'}</td>
                    <td className="px-3 py-2 font-mono">{tag.alarm_high ?? '—'}</td>
                    <td className="px-3 py-2 font-mono">{tag.warn_low ?? '—'}</td>
                    <td className="px-3 py-2 font-mono">{tag.warn_high ?? '—'}</td>
                    <td className="px-3 py-2">{tag.is_active ? '✓' : '✗'}</td>
                    <td className="px-3 py-2">
                      <button onClick={() => setEditing({ ...tag })} className="text-xs px-2 py-1 rounded bg-brand-700 text-white">Edit</button>
                    </td>
                  </tr>
                )
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

// ─── User Settings ────────────────────────────────────────────────────────────
type EditUserForm = { name: string; email: string; role_id: number; password: string };

function UserSettings({ qc }: WithQC) {
  const { data } = useQuery<User[]>({
    queryKey: ['users'],
    queryFn: () => authApi.listUsers().then((r) => r.data.users),
  });

  const { data: rolesData } = useQuery<{ id: number; name: string }[]>({
    queryKey: ['plant-roles'],
    queryFn: () => authApi.listRoles().then((r) => r.data.roles),
    staleTime: 60 * 60_000,
  });

  const users = data ?? [];
  const roles = rolesData ?? [];

  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState<CreateUserForm>({ name: '', email: '', password: '', role_id: roles[0]?.id ?? 2 });
  const [editing, setEditing] = useState<(EditUserForm & { id: string }) | null>(null);

  const createMut = useMutation({
    mutationFn: (d: CreateUserForm) => authApi.createUser(d),
    onSuccess: () => {
      toast.success('User created');
      qc.invalidateQueries({ queryKey: ['users'] });
      setShowCreate(false);
      setForm({ name: '', email: '', password: '', role_id: roles[0]?.id ?? 2 });
    },
    onError: () => toast.error('Failed to create user'),
  });

  const updateMut = useMutation({
    mutationFn: ({ id, ...d }: EditUserForm & { id: string }) => {
      const payload: Partial<User> & { password?: string } = {
        name: d.name || undefined,
        email: d.email || undefined,
        role_id: d.role_id,
      };
      if (d.password) payload.password = d.password;
      return authApi.updateUser(id, payload);
    },
    onSuccess: () => {
      toast.success('User updated');
      qc.invalidateQueries({ queryKey: ['users'] });
      setEditing(null);
    },
    onError: () => toast.error('Failed to update user'),
  });

  const toggleMut = useMutation({
    mutationFn: ({ id, is_active }: { id: string; is_active: boolean }) =>
      authApi.updateUser(id, { is_active }),
    onSuccess: () => {
      toast.success('Updated');
      qc.invalidateQueries({ queryKey: ['users'] });
    },
  });

  const fmtRole = (name: string) =>
    name.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());

  type FormTextField = 'name' | 'email' | 'password';
  const textFields: [string, FormTextField, string][] = [
    ['Name', 'name', 'text'],
    ['Email', 'email', 'email'],
    ['Password', 'password', 'password'],
  ];

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-xs text-slate-500">{users.length} user{users.length !== 1 ? 's' : ''} on this plant</p>
        <button onClick={() => { setShowCreate((s) => !s); setEditing(null); }} className="btn-primary text-sm">
          {showCreate ? 'Cancel' : '+ New User'}
        </button>
      </div>

      {showCreate && (
        <div className="card max-w-sm space-y-3">
          {textFields.map(([l, k, t]) => (
            <div key={k}>
              <label className="block text-xs text-slate-500 mb-1">{l}</label>
              <input
                type={t}
                value={form[k]}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  setForm((f) => ({ ...f, [k]: e.target.value }))
                }
                className="input"
              />
            </div>
          ))}
          <div>
            <label className="block text-xs text-slate-500 mb-1">Role</label>
            <select
              value={form.role_id}
              onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
                setForm((f) => ({ ...f, role_id: +e.target.value }))
              }
              className="input"
            >
              {roles.map((r) => (
                <option key={r.id} value={r.id}>{fmtRole(r.name)}</option>
              ))}
            </select>
          </div>
          <button
            onClick={() => createMut.mutate(form)}
            disabled={createMut.isPending}
            className="btn-primary w-full"
          >
            {createMut.isPending ? 'Creating...' : 'Create User'}
          </button>
        </div>
      )}

      {/* Edit user panel */}
      {editing && (
        <div className="card max-w-sm space-y-3 border-brand-400 border">
          <p className="text-xs font-semibold text-slate-700">Edit User</p>
          {(['name', 'email'] as const).map((k) => (
            <div key={k}>
              <label className="block text-xs text-slate-500 mb-1 capitalize">{k}</label>
              <input
                type={k === 'email' ? 'email' : 'text'}
                value={editing[k]}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  setEditing((s) => s ? { ...s, [k]: e.target.value } : s)
                }
                className="input"
              />
            </div>
          ))}
          <div>
            <label className="block text-xs text-slate-500 mb-1">Role</label>
            <select
              value={editing.role_id}
              onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
                setEditing((s) => s ? { ...s, role_id: +e.target.value } : s)
              }
              className="input"
            >
              {roles.map((r) => (
                <option key={r.id} value={r.id}>{fmtRole(r.name)}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs text-slate-500 mb-1">New Password <span className="text-slate-400">(leave blank to keep current)</span></label>
            <input
              type="password"
              value={editing.password}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                setEditing((s) => s ? { ...s, password: e.target.value } : s)
              }
              className="input"
              placeholder="Leave blank to keep unchanged"
            />
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => updateMut.mutate(editing)}
              disabled={updateMut.isPending}
              className="btn-primary flex-1"
            >
              {updateMut.isPending ? 'Saving...' : 'Save Changes'}
            </button>
            <button onClick={() => setEditing(null)} className="px-4 py-2 rounded-lg bg-slate-100 text-slate-700 text-sm">
              Cancel
            </button>
          </div>
        </div>
      )}

      <div className="card p-0 overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-hydro-border text-xs text-slate-500 uppercase tracking-wider">
              {['Name','Email','Role','Status','Last Login','Actions'].map((h) => (
                <th key={h} className="px-4 py-3 text-left">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {users.length === 0 ? (
              <tr><td colSpan={6} className="px-4 py-8 text-center text-slate-400 text-xs">No users assigned to this plant</td></tr>
            ) : users.map((u) => (
              <tr key={u.id} className={`table-row text-xs ${editing?.id === u.id ? 'bg-brand-50' : ''}`}>
                <td className="px-4 py-3 font-medium text-slate-900">{u.name}</td>
                <td className="px-4 py-3 text-slate-500">{u.email}</td>
                <td className="px-4 py-3">{fmtRole(u.role)}</td>
                <td className="px-4 py-3">
                  <span className={u.is_active ? 'badge-ok' : 'badge-crit'}>
                    {u.is_active ? 'Active' : 'Disabled'}
                  </span>
                </td>
                <td className="px-4 py-3 text-slate-400">
                  {u.last_login_at ? formatBSDate(new Date(u.last_login_at)) : 'Never'}
                </td>
                <td className="px-4 py-3 flex gap-1.5">
                  <button
                    onClick={() => {
                      setShowCreate(false);
                      setEditing({ id: u.id, name: u.name, email: u.email, role_id: u.role_id, password: '' });
                    }}
                    className="text-xs px-2 py-1 rounded bg-brand-600 text-white hover:bg-brand-700"
                  >
                    Edit
                  </button>
                  <button
                    onClick={() => toggleMut.mutate({ id: u.id, is_active: !u.is_active })}
                    className="text-xs px-2 py-1 rounded bg-slate-100 hover:bg-slate-200 text-slate-700"
                  >
                    {u.is_active ? 'Disable' : 'Enable'}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ─── Notification Settings ────────────────────────────────────────────────────
function NotificationSettings({ qc }: WithQC) {
  const { data } = useQuery<NotificationSetting[]>({
    queryKey: ['notifications'],
    queryFn: () => adminApi.notifications().then((r) => r.data.settings),
  });

  const mut = useMutation({
    mutationFn: ({ id, ...d }: Partial<NotificationSetting> & { id: number }) =>
      adminApi.updateNotification(id, d),
    onSuccess: () => {
      toast.success('Saved');
      qc.invalidateQueries({ queryKey: ['notifications'] });
    },
  });

  return (
    <div className="space-y-4 max-w-lg">
      {(data ?? []).map((setting) => (
        <div key={setting.id} className="card space-y-3">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-semibold text-slate-900 capitalize">{setting.type} Notifications</h3>
            <button
              onClick={() => mut.mutate({ id: setting.id, is_active: !setting.is_active })}
              className={`text-xs px-3 py-1 rounded-full transition-colors ${
                setting.is_active ? 'bg-emerald-600 text-white' : 'bg-slate-100 text-slate-500'
              }`}
            >
              {setting.is_active ? 'Enabled' : 'Disabled'}
            </button>
          </div>
          <p className="text-xs text-slate-600 font-mono bg-slate-50 rounded p-3 overflow-x-auto border border-hydro-border">
            {JSON.stringify(setting.config, null, 2)}
          </p>
        </div>
      ))}
    </div>
  );
}
